const getApiUrl = (): string => "/api/v1/webhooks/midtrans";

const getApiBase = (): string => "/api/v1";

export const getAuthToken = (): string => {
  if (typeof window !== "undefined" && window.sessionStorage) {
    return sessionStorage.getItem("auth_token") || "";
  }
  return "";
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const res = await fetch(`${getApiBase()}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ email: username, password: password }),
    });

    const data = await res.json();
    if (res.ok && data.token) {
      sessionStorage.setItem("auth_token", data.token);
      sessionStorage.setItem("session", "true");
      return { success: true };
    }

    return { success: false, error: data.error || data.message || "Invalid credentials" };
  } catch (error) {
    return { success: false, error: "Network error connecting to auth service" };
  }
};

export const logout = async () => {
  try {
    await fetch(`${getApiBase()}/auth/logout`, {
      method: "POST",
      headers: getAuthHeaders(),
    });
  } catch {
    // Ignore network failure on logout
  } finally {
    sessionStorage.removeItem("auth_token");
    sessionStorage.removeItem("session");
  }
};

export const getData = async (mode?: string) => {
  const baseUrl = getApiUrl();
  const url = mode ? `${baseUrl}?mode=${mode}` : baseUrl;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });
    if (response.status === 401) {
      logout();
      window.location.reload();
      return null;
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Failed to fetch notification data:", error);
    return [];
  }
};

export const syncPayload = async (orderID: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const res = await fetch(`${getApiUrl()}/${encodeURIComponent(orderID)}/sync`, {
      method: "POST",
      headers: getAuthHeaders(),
    });
    const data = await res.json();
    if (res.ok) return { success: true };
    return { success: false, error: data.message || "Sync gagal" };
  } catch (error) {
    return { success: false, error: "Network error" };
  }
};

export const deleteData = async () => {
  try {
    const response = await fetch(getApiUrl(), {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    return null;
  }
};

export const deleteDataById = async (id: string) => {
  try {
    const response = await fetch(`${getApiUrl()}/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    return null;
  }
};

// ── Webhook Destinations ────────────────────────────────────────────────────

export type DestinationMode = "production" | "sandbox";

export type WebhookDestination = {
  ID: number;
  Name: string;
  URL: string;
  Token: string;
  HeaderKey: string;
  HeaderValue: string;
  Mode: DestinationMode;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt: string;
};

export type WebhookDestinationPayload = {
  name: string;
  url: string;
  token: string;
  header_key: string;
  header_value: string;
  mode: DestinationMode;
  is_active: boolean;
};

const destUrl = () => `${getApiBase()}/webhook-destinations`;

export const getDestinations = async (): Promise<WebhookDestination[]> => {
  try {
    const res = await fetch(destUrl(), {
      headers: getAuthHeaders(),
    });
    if (res.status === 401) {
      logout();
      window.location.reload();
      return [];
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

export const createDestination = async (
  payload: WebhookDestinationPayload
): Promise<WebhookDestination | null> => {
  try {
    const res = await fetch(destUrl(), {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || "Failed to create destination");
    }
    return res.json();
  } catch (err: any) {
    alert(err.message || "Failed to create destination");
    return null;
  }
};

export const updateDestination = async (
  id: number,
  payload: WebhookDestinationPayload
): Promise<WebhookDestination | null> => {
  try {
    const res = await fetch(`${destUrl()}/${id}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || "Failed to update destination");
    }
    return res.json();
  } catch (err: any) {
    alert(err.message || "Failed to update destination");
    return null;
  }
};

export const deleteDestination = async (id: number): Promise<boolean> => {
  try {
    const res = await fetch(`${destUrl()}/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    return res.ok;
  } catch {
    return false;
  }
};
